import { useState } from "react";
import { useApp } from "@/context/AppContext";
import { getHeld, saveHeld } from "@/lib/tpv";
import { round2, uuid, vals } from "@/lib/format";
import { parseScaleBarcode, round3, weighPrice, weighedLine } from "@/lib/weigh";

export default function useTicket() {
  const { view, money } = useApp();
  const [cart, setCart] = useState([]);
  const [sel, setSel] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const [held, setHeldS] = useState(getHeld);
  const [weighing, setWeighing] = useState(null);

  const add = (p, qty = 1) => {
    if (p.sold_by_weight) { setWeighing(p); return; }
    setSel(p.id);
    setCart((c) => (c.some((l) => l.product_id === p.id)
      ? c.map((l) => (l.product_id === p.id ? { ...l, qty: l.qty + qty } : l))
      : [...c, { product_id: p.id, name: p.name, price: p.price, cost: p.cost || 0, qty }]));
  };
  const addWeighed = (p, kg, total) => {
    setSel(p.id);
    setCart((c) => {
      const old = c.find((l) => l.product_id === p.id);
      if (!old) return [...c, weighedLine(p, kg, total)];
      return c.map((l) => (l === old ? weighedLine(p, round3(old.qty + kg), round2(old.price * old.qty + total)) : l));
    });
  };
  const resolve = (code, qty = 1) => {
    const p = vals(view.products).find((x) => !x.deleted && x.barcode && x.barcode === code);
    if (p) { add(p, qty); return p.name; }
    const w = parseScaleBarcode(code, view.shop, view.products);
    if (w) { addWeighed(w.product, w.kg, w.total); return `${w.product.name} · ${w.kg.toFixed(3)} kg · ${money(w.total)}`; }
    return null;
  };
  const applyLine = (l, patch) => {
    const n = { ...l, ...patch };
    const unit = n.ppk ? weighPrice(n.qty, n.ppk, view.shop) / n.qty : (n.base ?? l.price);
    return { ...n, ...(n.ppk ? {} : { base: unit }), price: unit * (1 - (Number(n.disc) || 0) / 100) };
  };
  const patchLine = (id, patch) => setCart((c) => c.map((l) => (l.product_id === id ? applyLine(l, patch) : l)));
  const setQty = (id, qty) => {
    if (qty <= 0 && id === sel) setSel(null);
    setCart((c) => (qty <= 0 ? c.filter((l) => l.product_id !== id) : c.map((l) => (l.product_id === id ? applyLine(l, { qty }) : l))));
  };
  const reset = () => { setCart([]); setSel(null); setCustomerId(null); };
  const storeHeld = (list) => { setHeldS(list); saveHeld(list); };
  const hold = () => {
    if (!cart.length) return false;
    const label = (customerId && view.customers[customerId]?.name) || `#${held.length + 1}`;
    storeHeld([{ id: uuid(), at: new Date().toISOString(), label, cart, customerId }, ...held]);
    reset();
    return true;
  };
  const recall = (h) => {
    const rest = held.filter((x) => x.id !== h.id);
    storeHeld(cart.length ? [{ id: uuid(), at: new Date().toISOString(), label: `#${rest.length + 1}`, cart, customerId }, ...rest] : rest);
    setCart(h.cart); setCustomerId(h.customerId || null); setSel(null);
  };
  const removeHeld = (id) => storeHeld(held.filter((h) => h.id !== id));

  const subtotal = round2(cart.reduce((s, l) => s + l.price * l.qty, 0));
  const totals = { subtotal, discount: 0, total: subtotal, ticketDisc: 0 };
  return { cart, sel, setSel, customerId, setCustomerId, held, weighing, setWeighing, add, addWeighed, resolve, patchLine, setQty, reset, hold, recall, removeHeld, totals };
}
